import {
  ConfirmationModal,
  ControlledTextField,
} from '@issuemanager/apps/client/components';
import {
  SLAStateEnum,
  SLA_DESCRIPTION_LENGTH_MAX,
} from '@issuemanager/constants';
import { Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type SLAFormData = {
  message: string;
};

interface Props {
  isOpen: boolean;
  onAccept: (data: SLAFormData) => void;
  onClose: () => void;
  state: SLAStateEnum;
  isLoading?: boolean;
}

export function SLAConfirmModal({
  isOpen,
  onAccept,
  onClose,
  state,
  isLoading = false,
}: Props): React.JSX.Element {
  const { t } = useTranslation(['common', 'issue']);

  const form = useForm<SLAFormData>({
    mode: 'onSubmit',
    defaultValues: {
      message: '',
    },
  });

  const acceptButtonText = useMemo((): string => {
    if (isLoading) {
      return state === SLAStateEnum.DEACTIVATED
        ? t('issue:view.sla.modal.deactivating')
        : t('issue:view.sla.modal.activating');
    }
    return state === SLAStateEnum.DEACTIVATED
      ? t('issue:view.sla.modal.deactivateAccept')
      : t('issue:view.sla.modal.activateAccept');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, state]);

  const handleAccept = (): void => {
    form.handleSubmit((data) => {
      onAccept(data);
    })();
  };

  const handleClose = (): void => {
    form.reset({ message: '' });
    onClose();
  };

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onAccept={handleAccept}
      onCancel={handleClose}
      title={
        state === SLAStateEnum.DEACTIVATED
          ? t('issue:view.sla.modal.deactivateTitle')
          : t('issue:view.sla.modal.activateTitle')
      }
      description={
        <>
          <Typography variant='body1'>
            {state === SLAStateEnum.DEACTIVATED
              ? t('issue:view.sla.modal.deactivateSubtitle')
              : t('issue:view.sla.modal.activateSubtitle')}
          </Typography>
          <ControlledTextField
            name='message'
            control={form.control}
            rules={{
              required: t('issue:view.sla.errorMessages.message.required'),
            }}
            minRows={6}
            multiline
            label={
              state === SLAStateEnum.DEACTIVATED
                ? t('issue:view.sla.modal.deactivateMessage')
                : t('issue:view.sla.modal.activateMessage')
            }
            maxLength={SLA_DESCRIPTION_LENGTH_MAX}
            inputProps={{
              'data-testid': 'message-input',
            }}
          />
        </>
      }
      acceptButtonProps={{
        text: acceptButtonText,
        color: 'primary',
        loading: isLoading,
      }}
      cancelButtonProps={{
        text: t('form.cancel'),
        color: 'secondary',
        disabled: false,
      }}
    />
  );
}
